/**
 * Task/Instance Drawer Component
 *
 * Pull-out drawer (40% width from right) for viewing and editing task instances.
 * Includes all fields, category/tag selectors, user assignment, and file attachments.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  DatePicker,
  Typography,
  Divider,
  message,
  Spin,
  Popconfirm,
  Descriptions,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  InboxOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { instancesApi, templatesApi } from '@api/index';
import apiClient from '@api/client';
import { UserSelector } from '@components/selectors';
import FileList from '@components/files/FileList';
import type { ProcessStatus } from '@/types';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface TaskDrawerProps {
  open: boolean;
  instanceId?: string;
  templateId?: string; // For creating new instance
  mode: 'view' | 'edit' | 'create';
  onClose: () => void;
  onSaved?: () => void;
}

const statusLabels: Record<ProcessStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  postponed: 'Postponed',
  delayed: 'Delayed',
  cancelled: 'Cancelled',
  ignored: 'Ignored',
};

export default function TaskDrawer({
  open,
  instanceId,
  templateId,
  mode,
  onClose,
  onSaved,
}: TaskDrawerProps) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(mode === 'edit' || mode === 'create');
  const [postponeModalOpen, setPostponeModalOpen] = useState(false);
  const [postponeDate, setPostponeDate] = useState<dayjs.Dayjs | null>(null);
  const [postponeReason, setPostponeReason] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Fetch instance if editing/viewing
  const { data: instance, isLoading: instanceLoading } = useQuery({
    queryKey: ['instance', instanceId],
    queryFn: () => instancesApi.get(instanceId!),
    enabled: !!instanceId && open,
  });

  // Fetch template if creating new
  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => templatesApi.get(templateId!),
    enabled: !!templateId && mode === 'create' && open,
  });

  // Reset form when instance/template changes
  useEffect(() => {
    if (instance) {
      form.setFieldsValue({
        dueDate: dayjs(instance.dueDate),
        assignedUserId: instance.assignedUserId,
        completionNotes: instance.completionNotes,
      });
    } else if (template && mode === 'create') {
      form.setFieldsValue({
        dueDate: dayjs().add(7, 'days'),
        assignedUserId: template.defaultAssigneeId,
      });
    }
  }, [instance, template, form, mode]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: { templateId: string; dueDate: string; assignedUserId?: string }) =>
      instancesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      message.success('Task created successfully');
      onSaved?.();
      onClose();
    },
    onError: () => {
      message.error('Failed to create task');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { dueDate?: string; status?: ProcessStatus; assignedUserId?: string }) =>
      instancesApi.update(instanceId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      queryClient.invalidateQueries({ queryKey: ['instance', instanceId] });
      message.success('Task updated successfully');
      setIsEditing(false);
      onSaved?.();
    },
    onError: () => {
      message.error('Failed to update task');
    },
  });

  const startMutation = useMutation({
    mutationFn: () => instancesApi.start(instanceId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      queryClient.invalidateQueries({ queryKey: ['instance', instanceId] });
      message.success('Task started');
      onSaved?.();
    },
    onError: () => {
      message.error('Failed to start task');
    },
  });

  const completeMutation = useMutation({
    mutationFn: (notes?: string) => instancesApi.complete(instanceId!, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      queryClient.invalidateQueries({ queryKey: ['instance', instanceId] });
      message.success('Task completed!');
      onSaved?.();
    },
    onError: () => {
      message.error('Failed to complete task');
    },
  });

  const postponeMutation = useMutation({
    mutationFn: ({ newDueDate, reason }: { newDueDate: string; reason?: string }) =>
      instancesApi.postpone(instanceId!, newDueDate, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      queryClient.invalidateQueries({ queryKey: ['instance', instanceId] });
      message.success('Task postponed');
      setPostponeModalOpen(false);
      setPostponeDate(null);
      setPostponeReason('');
      onSaved?.();
    },
    onError: () => {
      message.error('Failed to postpone task');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => instancesApi.delete(instanceId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      message.success('Task deleted');
      onClose();
    },
    onError: () => {
      message.error('Failed to delete task');
    },
  });

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();

      if (mode === 'create' && templateId) {
        createMutation.mutate({
          templateId,
          dueDate: values.dueDate.format('YYYY-MM-DD'),
          assignedUserId: values.assignedUserId,
        });
      } else if (instanceId) {
        updateMutation.mutate({
          dueDate: values.dueDate?.format('YYYY-MM-DD'),
          assignedUserId: values.assignedUserId,
        });
      }
    } catch {
      // Form validation errors
    }
  }, [form, mode, templateId, instanceId, createMutation, updateMutation]);

  const handleComplete = useCallback(() => {
    const notes = form.getFieldValue('completionNotes');
    completeMutation.mutate(notes);
  }, [form, completeMutation]);

  const handlePostpone = useCallback(() => {
    if (!postponeDate) {
      message.error('Please select a new due date');
      return;
    }
    postponeMutation.mutate({
      newDueDate: postponeDate.format('YYYY-MM-DD'),
      reason: postponeReason,
    });
  }, [postponeDate, postponeReason, postponeMutation]);

  const isLoading = instanceLoading || templateLoading;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const currentTemplate = instance?.template || template;

  const drawerWidth = typeof window !== 'undefined' ? Math.max(window.innerWidth * 0.4, 400) : 600;

  const drawerTitle = mode === 'create' ? 'Create New Task' : isEditing ? 'Edit Task' : 'Task Details';
  const drawerEyebrow = mode === 'create' ? 'NEW TASK' : instanceId ? `TASK · ${instanceId.slice(0, 8).toUpperCase()}` : 'TASK';

  return (
    <Drawer
      title={
        <div style={{ borderBottom: '1px solid var(--border-default, #e4e7ec)', paddingBottom: 12 }}>
          <div style={{
            fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-tertiary, #9ca3af)',
            marginBottom: 4,
          }}>
            {drawerEyebrow}
          </div>
          <div style={{
            fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--text-primary, #111827)',
            lineHeight: 1.2,
          }}>
            {drawerTitle}
          </div>
        </div>
      }
      placement="right"
      width={drawerWidth}
      onClose={onClose}
      open={open}
      styles={{ header: { borderBottom: 'none', paddingBottom: 0 } }}
      extra={
        <Space>
          {!isEditing && mode !== 'create' && (
            <Button onClick={() => setIsEditing(true)} style={{ borderRadius: 2 }}>Edit</Button>
          )}
          {isEditing && (
            <>
              <Button onClick={() => setIsEditing(false)} icon={<CloseOutlined />} style={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSave}
                loading={isSaving}
                icon={<SaveOutlined />}
                style={{ borderRadius: 2, backgroundColor: 'var(--accent-ink, #1e40af)', borderColor: 'var(--accent-ink, #1e40af)' }}
              >
                Save
              </Button>
            </>
          )}
        </Space>
      }
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Header with template info */}
          {currentTemplate && (
            <div>
              <Title level={4} style={{ marginBottom: 8, fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}>
                {currentTemplate.title}
              </Title>
              {currentTemplate.description && (
                <Paragraph type="secondary">{currentTemplate.description}</Paragraph>
              )}
              <Space wrap>
                {currentTemplate.category && (
                  <span className="bp-tag-chip">
                    {currentTemplate.category.name}
                  </span>
                )}
                {currentTemplate.tags?.map((tag) => (
                  <span key={tag.id} className="bp-tag-chip">
                    <span className="bp-tag-dot" style={{ backgroundColor: tag.color || 'var(--text-tertiary)' }} />
                    {tag.name}
                  </span>
                ))}
              </Space>
            </div>
          )}

          {/* Status badge for existing instance */}
          {instance && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase' as const,
                letterSpacing: '0.08em',
                color: 'var(--text-tertiary, #9ca3af)',
              }}>Status</span>
              <span className={`bp-status-chip bp-status-${instance.status}`}>
                {statusLabels[instance.status]}
              </span>
              {instance.status !== 'completed' &&
                new Date(instance.dueDate) < new Date() && (
                  <span className="bp-status-chip bp-status-delayed">
                    <ExclamationCircleOutlined style={{ marginRight: 3 }} />
                    Overdue
                  </span>
                )}
            </div>
          )}

          <Divider style={{ margin: 0, borderColor: 'var(--border-subtle, #f0f2f5)' }} />

          {/* Form fields */}
          <Form form={form} layout="vertical" disabled={!isEditing}>
            <Form.Item
              name="dueDate"
              label={<span style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)' }}>Due Date</span>}
              rules={[{ required: true, message: 'Please select due date' }]}
            >
              <DatePicker style={{ width: '100%', borderRadius: 2 }} format="YYYY-MM-DD" className="bp-mono" />
            </Form.Item>

            <Form.Item
              name="assignedUserId"
              label={<span style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)' }}>Assigned To</span>}
            >
              <UserSelector placeholder="Search and select user..." />
            </Form.Item>

            {instance && (
              <Form.Item
                name="completionNotes"
                label={<span style={{ fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary, #9ca3af)' }}>Completion Notes</span>}
              >
                <TextArea
                  rows={3}
                  placeholder="Add notes about completion..."
                  disabled={instance.status === 'completed'}
                  style={{ borderRadius: 2 }}
                />
              </Form.Item>
            )}
          </Form>

          {/* Action buttons for existing instance */}
          {instance && instance.status !== 'completed' && !isEditing && (
            <>
              <Divider style={{ margin: 0, borderColor: 'var(--border-subtle, #f0f2f5)' }} />
              <Space direction="vertical" style={{ width: '100%' }}>
                {instance.status === 'pending' && (
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={() => startMutation.mutate()}
                    loading={startMutation.isPending}
                    block
                    style={{ borderRadius: 2, backgroundColor: 'var(--accent-ink, #1e40af)', borderColor: 'var(--accent-ink, #1e40af)' }}
                  >
                    Start Task
                  </Button>
                )}
                {(instance.status === 'pending' || instance.status === 'in_progress') && (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleComplete}
                    loading={completeMutation.isPending}
                    style={{ borderRadius: 2, backgroundColor: 'var(--status-success)', borderColor: 'var(--status-success)' }}
                    block
                  >
                    Mark Complete
                  </Button>
                )}
                <Button
                  icon={<ClockCircleOutlined />}
                  onClick={() => setPostponeModalOpen(true)}
                  block
                  style={{ borderRadius: 2 }}
                >
                  Postpone
                </Button>
              </Space>
            </>
          )}

          {/* Outline doc link */}
          {currentTemplate?.outlineDocUrl && (
            <>
              <Divider style={{ margin: 0, borderColor: 'var(--border-subtle, #f0f2f5)' }} />
              <div>
                <div style={{
                  fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--text-tertiary, #9ca3af)',
                  marginBottom: 8,
                }}>
                  Process Documentation
                </div>
                <div>
                  <Button
                    type="link"
                    icon={<LinkOutlined />}
                    href={currentTemplate.outlineDocUrl}
                    target="_blank"
                    style={{ padding: 0, color: 'var(--accent-ink, #1e40af)' }}
                  >
                    Open Documentation
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* File Attachments - only show for existing instances */}
          {instanceId && (
            <>
              <Divider style={{ margin: 0, borderColor: 'var(--border-subtle, #f0f2f5)' }} />
              <div>
                <div style={{
                  fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--text-tertiary, #9ca3af)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginBottom: 8,
                }}>
                  <FolderOutlined /> Attachments
                </div>
                <Upload.Dragger
                  name="file"
                  multiple
                  fileList={fileList}
                  listType="picture"
                  action={`${apiClient.defaults.baseURL}/v1/filemanager/upload?entityId=${instanceId}`}
                  withCredentials
                  onChange={(info) => {
                    setFileList(info.fileList);
                    const { status } = info.file;
                    if (status === 'done') {
                      const fileName = info.file.response?.name || info.file.name;
                      message.success(`${fileName} uploaded successfully.`);
                      queryClient.invalidateQueries({ queryKey: ['files', 'instance', instanceId] });
                    } else if (status === 'error') {
                      message.error(`${info.file.name} file upload failed.`);
                    }
                  }}
                  showUploadList={{
                    showRemoveIcon: true,
                    showDownloadIcon: true,
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to upload</p>
                  <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                    Attach files related to this task
                  </p>
                </Upload.Dragger>
                <div style={{ marginTop: 12 }}>
                  <FileList entityType="instance" entityId={instanceId} />
                </div>
              </div>
            </>
          )}

          {/* Instance details for existing */}
          {instance && (
            <>
              <Divider style={{ margin: 0, borderColor: 'var(--border-subtle, #f0f2f5)' }} />
              <Descriptions
                column={1}
                size="small"
                labelStyle={{
                  fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--text-tertiary, #9ca3af)',
                }}
                contentStyle={{
                  fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 12,
                  color: 'var(--text-secondary, #374151)',
                }}
              >
                <Descriptions.Item label="Original Due">
                  {new Date(instance.originalDueDate).toISOString().slice(0, 10)}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {new Date(instance.createdAt).toISOString().slice(0, 10)}
                </Descriptions.Item>
                {instance.completedAt && (
                  <Descriptions.Item label="Completed">
                    {new Date(instance.completedAt).toISOString().slice(0, 10)}
                  </Descriptions.Item>
                )}
                {instance.completedBy && (
                  <Descriptions.Item label="Completed By">
                    {instance.completedBy.fullName}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}

          {/* Delete button */}
          {instance && !isEditing && (
            <>
              <Divider style={{ margin: 0, borderColor: 'var(--border-subtle, #f0f2f5)' }} />
              <Popconfirm
                title="Delete this task?"
                description="This action cannot be undone."
                onConfirm={() => deleteMutation.mutate()}
                okText="Delete"
                okButtonProps={{ danger: true, style: { borderRadius: 2 } }}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteMutation.isPending}
                  block
                  style={{ borderRadius: 2 }}
                >
                  Delete Task
                </Button>
              </Popconfirm>
            </>
          )}
        </div>
      )}

      {/* Postpone Modal */}
      <Drawer
        title={
          <div style={{ borderBottom: '1px solid var(--border-default, #e4e7ec)', paddingBottom: 12 }}>
            <div style={{
              fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-tertiary, #9ca3af)',
              marginBottom: 4,
            }}>
              TASK ACTION
            </div>
            <div style={{
              fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--text-primary, #111827)',
            }}>
              Postpone Task
            </div>
          </div>
        }
        placement="right"
        width={400}
        onClose={() => setPostponeModalOpen(false)}
        open={postponeModalOpen}
        styles={{ header: { borderBottom: 'none', paddingBottom: 0 } }}
        extra={
          <Button
            type="primary"
            onClick={handlePostpone}
            loading={postponeMutation.isPending}
            style={{ borderRadius: 2, backgroundColor: 'var(--accent-ink, #1e40af)', borderColor: 'var(--accent-ink, #1e40af)' }}
          >
            Confirm
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <div style={{
              fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-tertiary, #9ca3af)',
              marginBottom: 8,
            }}>
              New Due Date
            </div>
            <DatePicker
              style={{ width: '100%', borderRadius: 2 }}
              value={postponeDate}
              onChange={setPostponeDate}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              format="YYYY-MM-DD"
              className="bp-mono"
            />
          </div>
          <div>
            <div style={{
              fontFamily: 'var(--font-mono, "IBM Plex Mono", monospace)',
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-tertiary, #9ca3af)',
              marginBottom: 8,
            }}>
              Reason (optional)
            </div>
            <TextArea
              style={{ borderRadius: 2 }}
              rows={3}
              placeholder="Why is this task being postponed?"
              value={postponeReason}
              onChange={(e) => setPostponeReason(e.target.value)}
            />
          </div>
        </Space>
      </Drawer>
    </Drawer>
  );
}
